import React, { useState } from 'react';
import { Settings, Save, Database, Bell, Shield, Globe } from 'lucide-react';

interface Configuracao {
  id: string;
  nome: string;
  valor: string | boolean | number;
  tipo: 'text' | 'number' | 'boolean' | 'select';
  descricao: string;
  categoria: string;
  opcoes?: string[];
}

const Configuracoes: React.FC = () => {
  const [configuracoes, setConfiguracoes] = useState<Configuracao[]>([
    {
      id: 'notificacoes_email',
      nome: 'Notificações por Email',
      valor: true,
      tipo: 'boolean',
      descricao: 'Habilitar envio de notificações por email',
      categoria: 'Notificações'
    },
    {
      id: 'notificacoes_slack',
      nome: 'Notificações Slack',
      valor: false,
      tipo: 'boolean',
      descricao: 'Habilitar notificações no Slack',
      categoria: 'Notificações'
    },
    {
      id: 'backup_automatico',
      nome: 'Backup Automático',
      valor: true,
      tipo: 'boolean',
      descricao: 'Executar backup automático diário',
      categoria: 'Sistema'
    },
    {
      id: 'retencao_logs',
      nome: 'Retenção de Logs (dias)',
      valor: 90,
      tipo: 'number',
      descricao: 'Período de retenção dos logs do sistema',
      categoria: 'Sistema'
    },
    {
      id: 'timeout_api',
      nome: 'Timeout da API (ms)',
      valor: 10000,
      tipo: 'number',
      descricao: 'Tempo limite para requisições da API',
      categoria: 'Sistema'
    },
    {
      id: 'ambiente',
      nome: 'Ambiente',
      valor: 'desenvolvimento',
      tipo: 'select',
      descricao: 'Ambiente de execução do sistema',
      categoria: 'Sistema',
      opcoes: ['desenvolvimento', 'homologacao', 'producao']
    },
    {
      id: 'max_upload_size',
      nome: 'Tamanho Máximo Upload (MB)',
      valor: 10,
      tipo: 'number',
      descricao: 'Tamanho máximo para upload de arquivos',
      categoria: 'Sistema'
    },
    {
      id: 'auditoria_ativa',
      nome: 'Auditoria Ativa',
      valor: true,
      tipo: 'boolean',
      descricao: 'Registrar todas as ações dos usuários',
      categoria: 'Segurança'
    },
    {
      id: 'sessao_timeout',
      nome: 'Timeout da Sessão (min)',
      valor: 30,
      tipo: 'number',
      descricao: 'Tempo de inatividade para logout automático',
      categoria: 'Segurança'
    }
  ]);

  const [salvando, setSalvando] = useState(false);
  const [categoriaAtiva, setCategoriaAtiva] = useState('Todas');

  const categorias = ['Todas', 'Notificações', 'Sistema', 'Segurança'];

  const handleConfigChange = (id: string, valor: string | boolean | number) => {
    setConfiguracoes(prev => 
      prev.map(config => 
        config.id === id ? { ...config, valor } : config
      )
    );
  };

  const salvarConfiguracoes = async () => {
    setSalvando(true);
    try {
      // Simular salvamento
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Configurações salvas:', configuracoes);
      // Aqui você implementaria a chamada para a API
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    } finally {
      setSalvando(false);
    }
  };

  const configuracaoPorCategoria = categoriaAtiva === 'Todas' 
    ? configuracoes 
    : configuracoes.filter(config => config.categoria === categoriaAtiva);

  const renderizarCampo = (config: Configuracao) => {
    switch (config.tipo) {
      case 'boolean':
        return (
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.valor as boolean}
              onChange={(e) => handleConfigChange(config.id, e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              {config.valor ? 'Ativado' : 'Desativado'}
            </span>
          </label>
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={config.valor as number}
            onChange={(e) => handleConfigChange(config.id, parseInt(e.target.value) || 0)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500-500 sm:text-sm"
          />
        );
      
      case 'select':
        return (
          <select
            value={config.valor as string}
            onChange={(e) => handleConfigChange(config.id, e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500-500 sm:text-sm"
          >
            {config.opcoes?.map(opcao => (
              <option key={opcao} value={opcao}>
                {opcao.charAt(0).toUpperCase() + opcao.slice(1)}
              </option>
            ))}
          </select>
        );
      
      default:
        return (
          <input
            type="text"
            value={config.valor as string}
            onChange={(e) => handleConfigChange(config.id, e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500-500 sm:text-sm"
          />
        );
    }
  };

  const getIconeCategoria = (categoria: string) => {
    switch (categoria) {
      case 'Notificações': return Bell;
      case 'Sistema': return Database;
      case 'Segurança': return Shield;
      default: return Settings;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
          <p className="text-gray-600">Configure o comportamento do sistema GMUD</p>
        </div>
        <button
          onClick={salvarConfiguracoes}
          disabled={salvando}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {salvando ? 'Salvando...' : 'Salvar Configurações'}
        </button>
      </div>

      {/* Filtros de categoria */}
      <div className="flex space-x-2">
        {categorias.map(categoria => (
          <button
            key={categoria}
            onClick={() => setCategoriaAtiva(categoria)}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              categoriaAtiva === categoria
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            {categoria}
          </button>
        ))}
      </div>

      {/* Lista de configurações */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {categoriaAtiva === 'Todas' ? 'Todas as Configurações' : `Configurações - ${categoriaAtiva}`}
          </h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {configuracaoPorCategoria.map((config) => {
            const IconeCategoria = getIconeCategoria(config.categoria);
            
            return (
              <div key={config.id} className="px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <IconeCategoria className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">{config.nome}</h4>
                        <p className="text-sm text-gray-500">{config.descricao}</p>
                        <div className="mt-2">
                          {renderizarCampo(config)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Informações adicionais */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Globe className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Informações Importantes</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>• As configurações são aplicadas imediatamente após o salvamento</p>
              <p>• Algumas mudanças podem requerer reinicialização do sistema</p>
              <p>• Recomenda-se testar as configurações em ambiente de desenvolvimento</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configuracoes;
